import React, { useState, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import useLocationName from '../../Hooks/useLocationName';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { FormProvider, useForm } from 'react-hook-form';
import { Fade } from '@mui/material';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import {
  endedCallDataAtom,
  openSnackbarAdviseAtom,
  selectedBranchAtom,
  selectedCustomerIdAtom,
  additionalCustomerDataAtom,
  activityTypesAtom,
} from '../../Atoms';
import CustomerSectionPage, {
  preloadCustomerSectionPage,
} from './LazyCustomerSectionPage';
import useAPI from '../../api';
import EmailWindow from './EmailSection/EmailWindow';
import TemplateMessageDialog from '../../Components/Dialog/TemplateMessageDialog';
import SmsAfterCallWrapper from './Shared/SmsAfterCallWrapper';
import groupTemplatesByName from '../../Helpers/Arrays/groupTemplatesByName';
import Chat from './ChatSection';
import CampaignSelectionDialog from './Shared/CampaignSelectionDialog';
import pickActiveContact from './Utils/pickActiveContact';
import useSendEmail from './Utils/useSendEmail';
import '../../Styles/app.scss';

const CustomerComponents = function CustomerComponents({
  sidebarOpen = false,
}) {
  const API = useAPI();
  const methods = useForm();
  const intl = useIntl();

  const locationName = useLocationName();
  const [customerId, setCustomerId] = useAtom(selectedCustomerIdAtom);
  const additionalCustomerData = useAtomValue(additionalCustomerDataAtom);
  const selectedBranch = useAtomValue(selectedBranchAtom);
  const setOpenSnackbarAdvise = useSetAtom(openSnackbarAdviseAtom);
  const activityTypes = useAtomValue(activityTypesAtom);

  const [campaignId, setCampaignId] = useState(null);
  const [campaignSelectionDialog, setCampaignSelectionDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [contactIndex, setContactIndex] = useState(0);
  const [endedCallData, setEndedCallData] = useAtom(endedCallDataAtom);
  const [chatOpen, setChatOpen] = useState(null);
  const [endCallMessage, setEndCallMessage] = useState(null);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState(null);
  const [selectedEmailAddress, setSelectedEmailAddress] = useState(null);
  const [emailWindowOpen, setEmailWindowOpen] = useState(null);
  const [sendEndCallMessage, setSendEndCallMessage] = useState(false);
  const [customerUnreadMessages, setCustomerUnreadMessages] = useState(0);
  const [smsSubmit, setSmsSubmit] = useState(false);

  const handleOpenEmailWindow = () => {
    setEmailWindowOpen('SEND');
  };
  const handleReplyMail = (data) => {
    setEmailWindowOpen(data);
  };
  const handleCloseEmailWindow = () => {
    setEmailWindowOpen(null);
  };

  const handleChangeContactIndex = (index) => {
    setContactIndex(index);
  };

  const getCustomer = useCallback(() => {
    if (customerId && (selectedBranch || additionalCustomerData?.branchId))
      //load the customer data
      API.getCustomer(
        customerId,
        additionalCustomerData?.branchId ?? selectedBranch.id,
        (result) => {
          if (result.status === 204) {
            setOpenSnackbarAdvise({
              message: intl.messages.CUSTOMER_NOT_FOUND,
              severity: 'error',
            });
            setCustomerId(null);
            return;
          }
          setSelectedCustomer(result);
        },
        (err) => {
          console.log(err);
        }
      );
  }, [customerId, selectedBranch, additionalCustomerData]);

  useEffect(() => {
    getCustomer();
  }, [customerId, selectedBranch]);

  // The workspace's code is loaded while the browser has nothing else to do, so opening a customer
  // is a render rather than a load. This overlay is mounted on every authenticated page, so the
  // waiting is done long before anyone clicks.
  useEffect(() => {
    const schedule = window.requestIdleCallback ?? window.setTimeout;
    const cancel = window.cancelIdleCallback ?? window.clearTimeout;
    const handle = schedule(() => preloadCustomerSectionPage(), {
      timeout: 3000,
    });

    return () => cancel(handle);
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      setSelectedPhoneNumber(
        pickActiveContact(selectedCustomer.contacts[contactIndex].contactPhones)
          ?.phoneNumber || null
      );
      setSelectedEmailAddress(
        selectedCustomer?.contacts[contactIndex]?.contactEmailAddresses?.find(
          ({ priority }) => priority
        )?.email || null
      );
    }
  }, [selectedCustomer, contactIndex]);

  useEffect(() => {
    if (selectedBranch && customerId) {
      API.getUnreadMessagesCount(
        customerId,
        (result) => {
          setCustomerUnreadMessages(result);
        },
        (err) => {
          console.log(err);
        }
      );
    }
  }, [customerId]);

  const changePhoneOrMail = (value, isPhone, isMobile, isNew) => {
    const updatedCustomer = { ...selectedCustomer };
    if (isPhone) {
      if (isNew) {
        if (value.isPrincipal) {
          if (isMobile) {
            updatedCustomer.mobilePhoneNumber = value.phoneNumber;
          } else {
            updatedCustomer.landlinePhoneNumber = value.phoneNumber;
          }
        }
        updatedCustomer.contacts?.[contactIndex]?.contactPhones.push({
          phoneNumber: value.phoneNumber,
          phoneTypeId: value.phoneTypeId,
          phoneType: value.phoneType,
          active: true,
          priority: value.priority,
        });
      } else {
        const phoneItem = updatedCustomer.contacts?.[
          contactIndex
        ]?.contactPhones.find(({ phoneNumber }) => phoneNumber === value);
        if (isMobile) {
          updatedCustomer.mobilePhoneNumber = phoneItem.active
            ? phoneItem.phoneNumber
            : null;
        } else {
          updatedCustomer.landlinePhoneNumber = phoneItem.active
            ? phoneItem.phoneNumber
            : null;
        }
      }
    } else if (isNew) {
      updatedCustomer.contacts?.[contactIndex]?.contactEmailAddresses.push({
        email: value.email,
        emailTypeId: value.emailTypeId,
        emailType: value.emailType,
        active: true,
        priority: value.priority,
      });
    } else {
      let item = updatedCustomer.contacts?.[
        contactIndex
      ]?.contactEmailAddresses.find(({ email }) => email === value);
      const index = updatedCustomer.contacts?.[
        contactIndex
      ]?.contactEmailAddresses.findIndex(({ email }) => email === value);
      item = Object.assign(item, { active: !item.active });
      updatedCustomer.contacts?.[contactIndex]?.contactEmailAddresses.splice(
        index,
        1,
        item
      );
    }
    setSelectedCustomer(updatedCustomer);
  };

  const handleSendEmail = useSendEmail({
    selectedEmailAddress,
    endCallMessage,
    setEndCallMessage,
    setSendEndCallMessage,
    onClose: handleCloseEmailWindow,
  });

  const handleOpenChat = (event) => {
    setChatOpen(event.currentTarget);
  };

  const handleCloseChat = () => {
    setChatOpen(null);
  };

  const DialActions = [
    {
      icon: <SmsOutlinedIcon />,
      name: 'SMS',
      onClick: handleOpenChat,
      badgeContent: customerUnreadMessages,
      disabled: false,
    },
    {
      icon: <EmailOutlinedIcon />,
      name: 'Email',
      onClick: handleOpenEmailWindow,
      disabled: false,
    },
  ];

  useEffect(() => {
    if (!customerId) {
      setSelectedCustomer(null); //unmounts customer details
      setContactIndex(0);
    }
  }, [customerId]);

  const checkSubmit = (value) => {
    setSmsSubmit(value);
  };

  const handleOpenCampaignSelectionDialog = () => {
    setCampaignSelectionDialog(true);
  };
  const handleCloseCampaignSelectionDialog = () => {
    setCampaignSelectionDialog(false);
  };

  useEffect(() => {
    if (!selectedCustomer) {
      setChatOpen(null);
      return;
    }
    if (selectedCustomer?.activeCampaigns?.length === 1) {
      setCampaignId(selectedCustomer.activeCampaigns[0].id);
      return;
    }
    if (selectedCustomer?.activeCampaigns?.length > 1) {
      handleOpenCampaignSelectionDialog();
    }
  }, [selectedCustomer]);

  const handleChangeCampaignId = (campaignId) => {
    setCampaignId(campaignId);
    handleCloseCampaignSelectionDialog();
  };

  useEffect(() => {
    if (!additionalCustomerData?.activityTypeId) return;
    if (
      additionalCustomerData?.activityTypeId ===
      activityTypes?.find((type) => type.name === 'SMS')?.id
    ) {
      setChatOpen(true);
    }
  }, [additionalCustomerData]);

  const open = Boolean(chatOpen);

  // The workspace is thousands of components and mounts in one go, on whatever task the customer
  // read resolves in. Deferred, that render is no longer urgent: the overlay and its chrome paint
  // first and the rest is built without holding the main thread against the next click.
  const workspaceCustomer = React.useDeferredValue(selectedCustomer);

  return (
    <>
      <Fade in={Boolean(customerId)} unmountOnExit>
        <div style={{ width: '100%', height: '100%' }}>
          {selectedCustomer && (
            <>
              <CampaignSelectionDialog
                open={campaignSelectionDialog}
                onClose={handleCloseCampaignSelectionDialog}
                activeCampaigns={selectedCustomer?.activeCampaigns || []}
                onSubmit={handleChangeCampaignId}
                campaignId={campaignId}
              />
              {workspaceCustomer && (
                <CustomerSectionPage
                  activeCampaigns={selectedCustomer?.activeCampaigns || []}
                  campaignId={campaignId}
                  handleOpenCampaignSelectionDialog={
                    handleOpenCampaignSelectionDialog
                  }
                  customerId={customerId}
                  openReply={handleReplyMail}
                  activityParentId={
                    //if from appointments section (the time was confirmed before getting here)
                    additionalCustomerData?.parentId &&
                    additionalCustomerData?.recallTime
                      ? additionalCustomerData.parentId
                      : //if from priorities section and activitytype is CALL
                        additionalCustomerData?.activityTypeId ===
                          activityTypes?.find((type) => type.name === 'CALL')
                            ?.id
                        ? additionalCustomerData?.parentId
                        : null
                  }
                  selectedPhoneNumber={selectedPhoneNumber}
                  setSelectedPhoneNumber={setSelectedPhoneNumber}
                  selectedEmailAddress={selectedEmailAddress}
                  setSelectedEmailAddress={setSelectedEmailAddress}
                  selectedCustomer={selectedCustomer}
                  contactIndex={contactIndex}
                  handleChangeContactIndex={handleChangeContactIndex}
                  reloadCustomer={getCustomer}
                  changePhoneOrMail={changePhoneOrMail}
                  actions={DialActions}
                  customerUnreadMessages={customerUnreadMessages}
                  sidebarOpen={sidebarOpen}
                  locationName={locationName}
                />
              )}

              <FormProvider {...methods}>
                <TemplateMessageDialog
                  id="sms-aftercall"
                  title={intl.messages.NEW_MESSAGE}
                  submitDisabled={!smsSubmit}
                  handleSubmit={() => setSendEndCallMessage(true)}
                  open={
                    endedCallData !== null &&
                    endedCallData?.outcome !== 9 && //wrong number
                    endedCallData?.outcome !== 18 && //inexistent number
                    endedCallData?.messageTemplates?.length > 0 &&
                    selectedPhoneNumber?.substring(0, 4) === '+393'
                  } //only if mobile phone number
                  handleClose={() => {
                    setEndedCallData(null);
                  }}
                  widgetWrapper={
                    <SmsAfterCallWrapper
                      failedChecks={endedCallData?.failedChecks}
                      checkSubmit={checkSubmit}
                      customerId={selectedCustomer?.id || customerId}
                      activityId={endedCallData?.id}
                      setEndCallMessage={setEndCallMessage}
                      selectedBranch={selectedBranch}
                      sendEndCallMessage={sendEndCallMessage}
                      templates={groupTemplatesByName(
                        endedCallData?.messageTemplates
                      )}
                      isEmailAddressAvailable={
                        selectedEmailAddress !== null && locationName !== 'SPOT'
                      }
                      customMessage={locationName === 'SPOT'}
                    />
                  }
                />
              </FormProvider>

              <Chat
                open={open}
                onClose={handleCloseChat}
                customerId={customerId}
                campaignId={campaignId}
                branchId={selectedBranch.id}
                customerUnreadMessages={customerUnreadMessages}
                setCustomerUnreadMessages={setCustomerUnreadMessages}
                endCallMessage={endCallMessage}
                setEndCallMessage={setEndCallMessage}
                setSendEndCallMessage={setSendEndCallMessage}
                setEndedCallData={setEndedCallData}
              />
              <EmailWindow
                open={Boolean(emailWindowOpen)}
                replyData={emailWindowOpen === 'SEND' ? null : emailWindowOpen}
                close={handleCloseEmailWindow}
                onSubmit={handleSendEmail}
                defaultEmail={selectedEmailAddress}
              />
            </>
          )}
        </div>
      </Fade>
    </>
  );
};

export default CustomerComponents;
